import { z } from 'zod';
import { DtoUserVoicePresetType } from '@/shared/constants';

export const VOICE_PRESET_STYLE_MAX_LENGTH = 20;

/**
 * 나만의 목소리 프리셋 (`/my-voice`) entity.
 *
 * BE `UserVoicePresetDto` (Phase B MS-4) 와 1:1 매핑:
 *   user_voice_preset (id, payload) + account_user_voice_preset (accountUserVoicePresetId, type, name, seq)
 *   + 권한 hint 4 종 (isMine / canEdit / canDelete / canDuplicate)
 *
 * 권한 hint 는 BE 단일 SoT — FE 가 직접 type/소유자 판단하지 않는다 (Phase 1 결정 4).
 */

/**
 * payload 8 필드 화이트리스트 (decisions-package #1 = C). BE `UserVoicePresetPayloadDto` 와 동기화.
 *
 * 옵셔널 필드는 모두 `.nullable().optional()` — BE `UserVoicePresetPayloadDto` 가
 * `@JsonInclude(NON_NULL)` 이라 null 옵셔널 필드는 응답 JSON 에 키 자체가 없다
 * (`UserVoicePresetMapper.payloadDtoToMap` 의 `objectMapper.convertValue` 가 클래스 레벨
 * `@JsonInclude` 를 존중해 Map 에서 자동 제외). FE 는 "키 없음" + 방어적으로 "null" 양쪽 허용.
 */
export const voicePresetPayloadSchema = z.object({
  code: z.string(),
  tempo: z.number(),
  pitch: z.number().nullable().optional(),
  emotionCode: z.string().nullable().optional(),
  style: z.string().nullable().optional(),
  audioTags: z.array(z.string()).nullable().optional(),
  description: z.string().nullable().optional(),
  languageCode: z.string().nullable().optional(),
});

export type VoicePresetPayload = z.infer<typeof voicePresetPayloadSchema>;

/** 프리셋 본체. SYSTEM(추천) / USER(내 것) 모두 같은 모양, 구분은 `type` 으로. */
export const voicePresetSchema = z.object({
  id: z.number(),
  accountUserVoicePresetId: z.number(),
  type: z.nativeEnum(DtoUserVoicePresetType),
  name: z.string(),
  seq: z.number(),
  payload: voicePresetPayloadSchema,
  isMine: z.boolean(),
  canEdit: z.boolean(),
  canDelete: z.boolean(),
  canDuplicate: z.boolean(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});

export type VoicePreset = z.infer<typeof voicePresetSchema>;

/**
 * 카드 mode — 같은 컴포넌트를 3 개 진입점(`/my-voice`, TtsDialog, BrandTemplate AiVoiceSettingPanel) 에서 재사용.
 * - `manage`: 케밥 메뉴 노출, USER 만 편집/삭제 (BE 권한 hint 사용)
 * - `pick`:   카드 클릭 → onSelect (TtsDialog '내 목소리' 탭)
 * - `apply`:  카드 클릭 → onApply (BrandTemplate 적용 흐름)
 */
export type VoicePresetCardMode = 'manage' | 'pick' | 'apply';

export type VoicePresetCardProps = {
  preset: VoicePreset;
  mode: VoicePresetCardMode;
  /** 외부에서 단일 재생 상태 관리. 현재 재생 중인 프리셋 id 와 일치 시 Pause 아이콘. */
  playingPresetId?: number;
  /** PlayArrow 클릭 — 부모가 voicePreview API 호출·다른 카드 정지 책임. */
  onPlay: (preset: VoicePreset) => void;
  /** pick 모드에서 카드 본체 클릭. */
  onSelect?: (preset: VoicePreset) => void;
  /** 편집 (manage + canEdit). */
  onEdit?: (preset: VoicePreset) => void;
  /** 복제 (manage + canDuplicate — SYSTEM 도 가능). */
  onDuplicate?: (preset: VoicePreset) => void;
  /** 삭제 (manage + canDelete). */
  onDelete?: (preset: VoicePreset) => void;
  /** apply 모드에서 카드 본체 클릭. */
  onApply?: (preset: VoicePreset) => void;
  /**
   * pick / apply 모드에서 "현재 선택됨" 시각 강조 (border-sys-style-primary-default).
   * TtsDialog '내 목소리' 탭에서 `defaultPresetId` 전달 시 매칭 카드에 부착.
   */
  isSelected?: boolean;
  /** DnD reorder 등 커스텀 클래스. */
  className?: string;
};
